import type { ReactElement } from 'react';
import { Users } from 'lucide-react';
import type { MCPServerListItem } from '@kirin/api-client/contracts/mcp';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import {
  MCPNameCell,
  MCPToolsCell,
  MCPTypeBadge,
  MCPTransportBadge,
  MCPVersionCell,
  MCPStatusIndicator,
  MCPSourceBadge,
  McpRiskDetailsCard,
  McpRiskUnavailable,
} from '@/components/shared/mcp';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export function SourceCell({ row }: { row: MCPServerListItem }): ReactElement {
  return <MCPSourceBadge provenance={row.provenance} />;
}

export function NameCell({ row }: { row: MCPServerListItem }): ReactElement {
  return (
    <MCPNameCell name={row.name} serverName={row.serverName} url={row.url} />
  );
}

export function ToolsCell({ row }: { row: MCPServerListItem }): ReactElement {
  return <MCPToolsCell serverName={row.name} tools={row.tools} />;
}

export function VersionCell({ row }: { row: MCPServerListItem }): ReactElement {
  return <MCPVersionCell row={row} />;
}

export function PublisherCell({
  row,
}: {
  row: MCPServerListItem;
}): ReactElement {
  return (
    <span className="text-muted-foreground break-words whitespace-normal leading-snug">
      {row.publisher ?? '—'}
    </span>
  );
}

export function TypeCell({ row }: { row: MCPServerListItem }): ReactElement {
  return <MCPTypeBadge type={row.type} />;
}

export function TransportCell({
  row,
}: {
  row: MCPServerListItem;
}): ReactElement {
  return <MCPTransportBadge transport={row.transport} />;
}

interface UsersCellProps {
  row: MCPServerListItem;
  onSelectServer?: (serverName: string) => void;
}

export function UsersCell({
  row,
  onSelectServer,
}: UsersCellProps): ReactElement {
  // KIR-3940 (D2): gateway-managed rows have no per-user attribution. Detect
  // via the explicit provenance signal, not by treating a null userCount as
  // "no data" — a real endpoint row can also report 0.
  if (row.provenance === 'gateway') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1.5 font-medium text-muted-foreground cursor-default"
            aria-label="Per-user attribution not available for gateway-managed servers"
          >
            <Users className="h-3.5 w-3.5" />—
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Per-user attribution is not available for gateway-managed servers.
        </TooltipContent>
      </Tooltip>
    );
  }

  const userCount = row.userCount ?? 0; // Endpoint rows always report a number.
  const count = (
    <span className="inline-flex items-center gap-1.5 font-medium text-muted-foreground">
      <Users className="h-3.5 w-3.5" />
      {userCount}
    </span>
  );

  if (!onSelectServer) {
    return count;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={event => {
            // Gateway rows are click-to-expand (KIR-3940 slice 1); without
            // this the row would also toggle expansion on every click.
            event.stopPropagation();
            onSelectServer(row.name);
          }}
          className="inline-flex items-center gap-1.5 font-medium text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors cursor-pointer"
        >
          <Users className="h-3.5 w-3.5" />
          {userCount}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Users className="h-3 w-3" />
            <span>
              {userCount} user{userCount !== 1 ? 's' : ''} with this MCP server
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Click to see user list
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export function StatusCell({ row }: { row: MCPServerListItem }): ReactElement {
  // KIR-3940 (D2): gateway-managed rows carry no running-instance attribution.
  // Same argument as UsersCell above — coercing the null to 0 would render
  // "Inactive / No instances currently running", which asserts an idle server
  // rather than an unmeasured one. Branch on the provenance signal, not on the
  // null: an endpoint row can legitimately report 0 and *is* Inactive.
  if (row.provenance === 'gateway') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="text-muted-foreground cursor-default"
            aria-label="Running-instance status not available for gateway-managed servers"
          >
            —
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Running-instance status is not available for gateway-managed servers.
        </TooltipContent>
      </Tooltip>
    );
  }

  // Endpoint rows always report a number.
  return (
    <MCPStatusIndicator
      runningCount={row.runningCount ?? 0}
      derivedRunningCount={row.derivedRunningCount}
    />
  );
}

export function FirstSeenCell({
  row,
}: {
  row: MCPServerListItem;
}): ReactElement {
  return <RelativeTimeWithTooltip date={row.firstSeenAt} />;
}

export function LastSeenCell({
  row,
}: {
  row: MCPServerListItem;
}): ReactElement {
  return <RelativeTimeWithTooltip date={row.lastSeenAt} />;
}

/**
 * KIR-5108 Risk cell: the row's AgentMesh value with its detail card, or a
 * dash explaining why there is none.
 */
export function RiskCell({
  row,
  organizationId,
}: {
  row: MCPServerListItem;
  organizationId: string;
}): ReactElement {
  if (!row.risk) {
    return <McpRiskUnavailable reason={row.riskUnavailableReason} />;
  }
  return (
    <McpRiskDetailsCard
      organizationId={organizationId}
      name={row.name}
      risk={row.risk}
    />
  );
}
